import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bool, func, object, string, array } from 'prop-types';
import classNames from 'classnames';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { findOptionsForSelectFilter } from '../../util/search';
import { getDefaultTimeZoneOnBrowser, isDate, timestampToDate } from '../../util/dates';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { getCreatorTier, CREATOR_SUBSCRIPTION_TIERS } from '../../util/subscriptions';
import {
  InlineTextButton,
  NamedLink,
  ListingLink,
  PrimaryButtonInline,
  PreviewListingButton,
  Modal,
  Icon,
} from '..';
import { EditLiveRoomSchedulesForm, RescheduleEpisodeForm } from '../../forms';
import { toLocalInputValue } from '../../forms/RescheduleEpisodeForm/RescheduleEpisodeForm';
import { rescheduleEpisode } from '../../containers/EditLiveRoomPage/EditLiveRoomPage.duck';
import config from '../../config';
import { features } from '../../config/features';
import moment from 'moment';

import AvailabilityExceptions from './AvailabilityExceptions';
import css from './EditLiveRoomSchedulesPanel.module.css';

const DEFAULT_AVAILABILITY_SEATS = 100;

// Episode lengths in 15-minute steps, in hours as the form expects. Free
// creators get 15 and 30 minutes (Yuko, 18 Oct 2026); Pro creators and
// organizations' Live Rooms get all of them.
const EPISODE_DURATIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5];
const FREE_EPISODE_MAX_HOURS = 0.5;

// Plans are off for Phase 1 (plan 6p): nothing to upgrade to, so these sentences stop before it
const usageMessageId = reached => {
  if (reached) {
    return features.paidPlans
      ? 'EditLiveRoomSchedulesPanel.usageReached'
      : 'EditLiveRoomSchedulesPanel.usageReachedNoPlans';
  }
  return features.paidPlans
    ? 'EditLiveRoomSchedulesPanel.usageNotice'
    : 'EditLiveRoomSchedulesPanel.usageNoticeNoPlans';
};
export const freeEpisodeLengthMessageId = () =>
  features.paidPlans
    ? 'EditLiveRoomSchedulesPanel.freeEpisodeLength'
    : 'EditLiveRoomSchedulesPanel.freeEpisodeLengthNoPlans';

const validateDate = date => (isDate(date) ? date : new Date(date));

const defaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

// Ensure that the AvailabilityExceptions are in sensible order.
//
// Note: if you allow fetching more than 100 exception,
// pagination kicks in and that makes client-side sorting impossible.
const sortExceptionsByStartTime = (a, b) => {
  return validateDate(a.attributes.start).getTime() - validateDate(b.attributes.start).getTime();
};

// Denormalise and format availabilityException repating dates
// that we get from the form values.
const denormaliseRepeatingDates = repeatingDates =>
  repeatingDates?.filter(d => d?.date?.date)?.map(d => d.date.date) || [];

//////////////////////////////////
// EditLiveRoomSchedulesPanel //
//////////////////////////////////
const EditLiveRoomSchedulesPanel = props => {
  const {
    intl,
    className,
    rootClassName,
    listing,
    availabilityExceptions,
    fetchExceptionsInProgress,
    onAddAvailabilityException,
    onDeleteAvailabilityException,
    onAddRepeatingAvailabilityExceptions,
    addExceptionInProgress,
    addRepeatingExceptionsInProgress,
    deleteExceptionId,
    deleteExceptionInProgress,
    disabled,
    onSubmit,
    onUpdateListing,
    onManageDisableScrolling,
    onGetPrevPathParams,
    onCreateMeeting,
    submitButtonText,
    updateInProgress,
    errors,
    currentUser,
  } = props;

  const attendeeUsage = useSelector(state => state.EditLiveRoomPage?.attendeeUsage);
  const dispatch = useDispatch();

  // Reschedule: the episode being moved, and the save state of its modal.
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleInProgress, setRescheduleInProgress] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);
  const closeReschedule = () => {
    setRescheduleTarget(null);
    setRescheduleError(null);
  };
  const submitReschedule = ({ start }) => {
    const { exception, metadata } = rescheduleTarget;
    const lengthMs =
      new Date(exception.attributes.end).getTime() - new Date(exception.attributes.start).getTime();
    const newStart = new Date(start);
    setRescheduleInProgress(true);
    setRescheduleError(null);
    return dispatch(
      rescheduleEpisode({
        listing: currentListing,
        episodeId: metadata?.attributes?.episodeId || metadata?.id || exception.id.uuid,
        start: newStart,
        end: new Date(newStart.getTime() + lengthMs),
      })
    )
      .then(() => {
        setRescheduleInProgress(false);
        closeReschedule();
      })
      .catch(e => {
        setRescheduleInProgress(false);
        setRescheduleError(e || true);
      });
  };

  // Hooks. Arriving from "Create > New live episode" opens the Add episode
  // modal straight away; the flag is read once, when the panel mounts.
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(
    () => typeof window !== 'undefined' && /[?&]newEpisode=1(?:&|$)/.test(window.location.search)
  );

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const { title, publicData } = currentListing.attributes;

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;

  const isOrganizationRoom = !!publicData?.organizationId;
  const isFreeCreator =
    !isOrganizationRoom && getCreatorTier(currentUser) === CREATOR_SUBSCRIPTION_TIERS.FREE;
  const durationConfigOptions = EPISODE_DURATIONS.filter(
    hours => !isFreeCreator || hours <= FREE_EPISODE_MAX_HOURS
  ).map(hours => ({
    value: hours,
    label: intl.formatMessage(
      { id: 'EditLiveRoomSchedulesPanel.durationMinutes' },
      { minutes: Math.round(hours * 60) }
    ),
  }));
  const usage = isFreeCreator && attendeeUsage?.limited ? attendeeUsage : null;
  const usageHours = minutes => Math.round((minutes / 60) * 10) / 10;

  const exceptionButtonDisabled = disabled || fetchExceptionsInProgress;
  const sortedAvailabilityExceptions = availabilityExceptions.sort(sortExceptionsByStartTime);
  const publicDataAvailabilityExceptions = publicData?.exceptions;
  // Set when an episode could not be completed (room or metadata step failed).
  const [saveEpisodeError, setSaveEpisodeError] = useState(null);
  // One object for the life of the panel. Final Form starts over whenever
  // initialValues changes, and a fresh literal on every render meant a failed
  // save wiped everything the creator had typed.
  const [episodeFormInitialValues] = useState(() => ({
    access: findOptionsForSelectFilter('access', config.custom.liveRoomFilters)[0].key,
    repeatingDates: [{}],
  }));
  const prevPathParams = onGetPrevPathParams();

  // Save exception metadata
  const saveExceptionMetadata = async (listingId, exceptionId, values, repeatingExceptions) => {
    const {
      exceptionStartDate,
      exceptionEndDate,
      exceptionStartTime,
      exceptionEndTime,
      repeatingDates,
      ...rest
    } = values;

    // The server wants the plain id; the SDK's UUID object does not survive
    // the trip (see api/whereby/create-meeting.js).
    const listingUuid = listingId?.uuid || listingId;

    const meetingRoom = await onCreateMeeting({
      roomNamePrefix: rest.title,
      endDate: exceptionEndDate.date,
      listingId: listingUuid,
    });
    // Room creation failed (Whereby down / missing keys / not the owner) —
    // abort so we don't persist an episode with no room that nobody can join.
    if (!meetingRoom || !meetingRoom.roomName) {
      throw new Error('Whereby room creation failed');
    }
    // Persist ONLY non-secret identifiers. Room URLs contain the moderator /
    // viewer room keys and must never live in world-readable publicData —
    // MeetPage fetches the authorized URL from the server (get-meeting) at
    // join time, after an entitlement check.
    const { roomName, meetingId } = meetingRoom;

    // additional information that we call exception metadata
    const exception = {
      id: exceptionId,
      type: 'exception',
      attributes: {
        // Milliseconds, like the repeats below. The form holds timestamp
        // strings, and a digit string stored here is an invalid date for
        // anything that reads it with new Date().
        start: timestampToDate(exceptionStartTime).valueOf(),
        end: timestampToDate(exceptionEndTime).valueOf(),
        timeZone: defaultTimeZone(),
        createdAt: Date.now(),
        meeting: {
          roomName,
          meetingId,
        },
        ...rest,
      },
    };

    const repeatingExceptionsMaybe = repeatingExceptions
      ? await Promise.all(
          repeatingExceptions.map(async e => {
            const repeatingMeetingRoom = await onCreateMeeting({
              roomNamePrefix: rest.title,
              endDate: e.attributes.end,
              listingId: listingUuid,
            });
            if (!repeatingMeetingRoom || !repeatingMeetingRoom.roomName) {
              throw new Error('Whereby room creation failed');
            }
            // Same as the main exception: store only non-secret identifiers.
            // (The old code spread the whole room object here — including the
            // raw host URL with its moderator key — into public data.)
            const {
              roomName: repeatingRoomName,
              meetingId: repeatingMeetingId,
            } = repeatingMeetingRoom;
            return {
              id: e.id.uuid,
              type: 'repeating-exception',
              attributes: {
                start: new Date(e.attributes.start).valueOf(),
                end: new Date(e.attributes.end).valueOf(),
                // The schedule needs it to book a repeat; without it RSVP on
                // any repeat of a series could never go through.
                timeZone: exception.attributes.timeZone,
                meeting: {
                  roomName: repeatingRoomName,
                  meetingId: repeatingMeetingId,
                },
                ...rest,

                // Additional attributes specific to repeating
                // availability exceptions.
                mainExceptionId: exception.id,
              },
            };
          })
        )
      : [];

    // Add exceptions to listing publicData
    const updatedExceptions = publicDataAvailabilityExceptions
      ? [exception, ...repeatingExceptionsMaybe, ...publicDataAvailabilityExceptions]
      : [exception, ...repeatingExceptionsMaybe];
    return onUpdateListing('schedules', {
      id: listingId,
      publicData: {
        exceptions: updatedExceptions,
      },
    });
  };

  // Save repeating exceptions
  const saveRepeatingExceptions = (exceptionStartTime, exceptionEndTime, repeatingDates) => {
    // Original exception start & end hour
    const momentStartHour = moment(exceptionStartTime).format('HH');
    const momentEndHour = moment(exceptionEndTime).format('HH');

    // Original exception start & end minutes
    const momentStartMinutes = moment(exceptionStartTime).format('mm');
    const momentEndMinutes = moment(exceptionEndTime).format('mm');

    // Format new availability exceptions from the `repeatingDates` array
    // of repeating dates
    const repeatingAvailabilityExceptions = repeatingDates.map(date => {
      const start = moment(date)
        .startOf('day')
        .add(momentStartHour, 'hour')
        .add(momentStartMinutes, 'minutes');

      const end = moment(date)
        .startOf('day')
        .add(momentEndHour, 'hour')
        .add(momentEndMinutes, 'minutes');

      return {
        listingId: listing.id.uuid,
        start: start.format(),
        end: end.format(),
        seats: DEFAULT_AVAILABILITY_SEATS,
      };
    });

    return onAddRepeatingAvailabilityExceptions({
      exceptions: repeatingAvailabilityExceptions,
    });
  };

  // An availability exception with no metadata is not an episode: it has no
  // title and no room, nobody can reserve a spot on it, yet it still shows on
  // the calendar. If the room or metadata step fails, take the exception(s)
  // back out so nothing half-saved is left behind, and tell the creator.
  const discardHalfSavedEpisode = async (exceptions, err) => {
    console.error('Failed to save episode meeting:', err);
    setSaveEpisodeError(err || true);
    await Promise.all(
      (exceptions || [])
        .filter(e => e?.id)
        .map(e => Promise.resolve(onDeleteAvailabilityException({ id: e.id })).catch(() => null))
    );
  };

  // Save exception click handler
  const saveException = (values, form) => {
    const { exceptionStartTime, exceptionEndTime, repeatingDates } = values;
    setSaveEpisodeError(null);

    // A creator's own episodes are created straight through Sharetribe, so the
    // Free allowance is applied here as well as on our server.
    if (usage?.reached && (values.access || 'free') === 'free') {
      setSaveEpisodeError({ error: 'free_plan_attendee_hours' });
      return Promise.resolve();
    }

    return onAddAvailabilityException({
      listingId: listing.id,
      seats: DEFAULT_AVAILABILITY_SEATS,
      start: timestampToDate(exceptionStartTime),
      end: timestampToDate(exceptionEndTime),
    })
      .then(async exceptionResponse => {
        // Denormalise repeating dates and check if the exception
        // should repeat
        const denormalisedRepeatingDates = denormaliseRepeatingDates(repeatingDates);
        const shouldRepeat = denormalisedRepeatingDates.length > 0;

        /** Run if exception should repeat */
        if (shouldRepeat) {
          // Add repeating exceptions
          return saveRepeatingExceptions(
            timestampToDate(exceptionStartTime),
            timestampToDate(exceptionEndTime),
            denormalisedRepeatingDates
          ).then(async exceptionsResponse => {
            try {
              // Save exception & repeating exceptions to publicData. Only close
              // the modal once the meeting rooms + metadata actually saved — a
              // failed room creation must not leave a room-less episode behind.
              await saveExceptionMetadata(
                currentListing.id,
                exceptionResponse.id.uuid,
                values,
                exceptionsResponse
              );
              setIsAvailabilityModalOpen(false);
              form.restart();
            } catch (err) {
              await discardHalfSavedEpisode(
                [
                  exceptionResponse,
                  ...(Array.isArray(exceptionsResponse) ? exceptionsResponse : []),
                ],
                err
              );
            }
          });
        } else {
          /** Run if exception shouldn't repeat */
          try {
            // Save exception to publicData (creates the Whereby room). Keep the
            // modal open on failure instead of silently closing.
            await saveExceptionMetadata(currentListing.id, exceptionResponse.id.uuid, values);
            setIsAvailabilityModalOpen(false);
            form.restart();
          } catch (err) {
            await discardHalfSavedEpisode([exceptionResponse], err);
          }
        }
      })
      .catch(e => {
        // Don't close modal if there was an error; a Free plan limit says which.
        if (e?.error === 'free_plan_attendee_hours' || e?.error === 'free_plan_episode_length') {
          setSaveEpisodeError(e);
        }
      });
  };

  // Delete exception click handler
  const deleteException = params => {
    onDeleteAvailabilityException(params).then(() => {
      const exceptionMetadata = publicDataAvailabilityExceptions?.find(
        e => e.id === params.id.uuid
      );

      if (exceptionMetadata) {
        // Update the listing
        const filteredExceptions = publicDataAvailabilityExceptions.filter(
          e => e.id !== params.id.uuid
        );
        return onUpdateListing('schedules', {
          id: currentListing.id,
          publicData: {
            exceptions: filteredExceptions,
          },
        });
      }
    });
  };

  // Errors
  const showListingError = errors.showListingsError ? (
    <p className={css.error}>
      <FormattedMessage id="EditLiveRoomSchedulesPanel.showListingFailed" />
    </p>
  ) : null;

  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditLiveRoomSchedulesPanel.title"
      values={{
        listingTitle: (
          <ListingLink className={css.listingLink} listing={listing} isLiveRoom>
            {title}
          </ListingLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage id="EditLiveRoomSchedulesPanel.createListingTitle" />
  );

  const repeatOptions = findOptionsForSelectFilter('repeat', config.custom.liveRoomFilters);
  const accessOptions = findOptionsForSelectFilter('access', config.custom.liveRoomFilters);

  return (
    <main className={classes}>
      <div className={css.form}>
        <div className={css.content}>
          <div className={css.contentWrapper}>
            <h1 className={css.title}>{panelTitle}</h1>
            <p className={css.subTitle}>
              <FormattedMessage id="EditLiveRoomSchedulesForm.subTitle" />
            </p>

            {showListingError}

            <div className={css.exceptionLegends}>
              <div className={css.exceptionLegend}>
                <span className={css.exceptionLegendColor} />
                <span className={css.exceptionLegendText}>
                  <FormattedMessage id="EditLiveRoomSchedulesPanel.legendFree" />
                </span>
              </div>
              <div className={css.exceptionLegend}>
                <span
                  className={classNames(css.exceptionLegendColor, css.exceptionLegendColorMembers)}
                />
                <span className={css.exceptionLegendText}>
                  <FormattedMessage id="EditLiveRoomSchedulesPanel.legendMembers" />
                </span>
              </div>
            </div>
            <AvailabilityExceptions
              intl={intl}
              timeZone={defaultTimeZone()}
              availabilityExceptions={sortedAvailabilityExceptions}
              publicDataAvailabilityExceptions={publicDataAvailabilityExceptions}
              membershipTiers={publicData?.memberships || []}
              fetchExceptionsInProgress={fetchExceptionsInProgress}
              deleteExceptionInProgress={deleteExceptionInProgress}
              deleteExceptionId={deleteExceptionId}
              onDeleteAvailabilityException={deleteException}
              onRescheduleAvailabilityException={(exception, metadata) =>
                setRescheduleTarget({ exception, metadata })
              }
            />
            {usage ? (
              <div
                className={classNames(css.usage, {
                  [css.usageNotice]: usage.notice && !usage.reached,
                  [css.usageReached]: usage.reached,
                })}
              >
                <div className={css.usageHeader}>
                  <span className={css.usageTitle}>
                    <FormattedMessage id="EditLiveRoomSchedulesPanel.usageTitle" />
                  </span>
                  <span className={css.usageCount}>
                    <FormattedMessage
                      id={
                        usage.reached
                          ? 'EditLiveRoomSchedulesPanel.usageCountReached'
                          : 'EditLiveRoomSchedulesPanel.usageCount'
                      }
                      values={{
                        used: usageHours(usage.usedMinutes),
                        limit: usageHours(usage.limitMinutes),
                      }}
                    />
                  </span>
                </div>
                <div
                  className={css.usageBar}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={usageHours(usage.limitMinutes)}
                  aria-valuenow={usageHours(usage.usedMinutes)}
                >
                  <span
                    className={css.usageBarFill}
                    style={{
                      width: `${Math.min(100, (usage.usedMinutes / usage.limitMinutes) * 100)}%`,
                    }}
                  />
                </div>
                {usage.notice || usage.reached ? (
                  <p className={css.usageText}>
                    <span className={css.usageIcon} aria-hidden="true">
                      <Icon name={usage.reached ? 'error' : 'warning'} size={16} />
                    </span>
                    <span>
                      <FormattedMessage
                        id={usageMessageId(usage.reached)}
                        values={{
                          left: usageHours(Math.max(0, usage.limitMinutes - usage.usedMinutes)),
                          resetDate: usage.resetsAt
                            ? intl.formatDate(usage.resetsAt, {
                                month: 'short',
                                day: 'numeric',
                                timeZone: 'UTC',
                              })
                            : '',
                          upgradeLink: features.paidPlans ? (
                            <NamedLink name="CreatorSettingsBillingPage" className={css.usageLink}>
                              <FormattedMessage id="EditLiveRoomSchedulesPanel.usageUpgrade" />
                            </NamedLink>
                          ) : null,
                        }}
                      />
                      {usage.reached ? (
                        <span className={css.usagePassNote}>
                          <FormattedMessage id="EditLiveRoomSchedulesPanel.usagePassNote" />
                        </span>
                      ) : null}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : null}
            <InlineTextButton
              className={css.addExceptionButton}
              onClick={() => setIsAvailabilityModalOpen(true)}
              disabled={exceptionButtonDisabled}
            >
              <FormattedMessage id="EditLiveRoomSchedulesPanel.addException" />
            </InlineTextButton>

            <div className={css.submitButtonRoot}>
              <NamedLink className={css.backButton} name="EditLiveRoomPage" params={prevPathParams}>
                <FormattedMessage id="EditLiveRoomSchedulesPanel.backButton" />
              </NamedLink>
              <div className={css.submitAndPreviewButton}>
                <PreviewListingButton listing={listing} />
                {!isPublished ? (
                  <PrimaryButtonInline
                    size="small"
                    checkmarkSize="small"
                    type="button"
                    onClick={() => {
                      // As we create timeslots with availability exceptions
                      // API, we don't need to send any data when the tab is
                      // is updated, but we need to call the function so that
                      // the listing can be published.
                      const updateValues = {};
                      onSubmit(updateValues);
                    }}
                  >
                    {submitButtonText}
                  </PrimaryButtonInline>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      {onManageDisableScrolling ? (
        <Modal
          id="EditAvailabilityForm"
          isOpen={isAvailabilityModalOpen}
          onClose={() => setIsAvailabilityModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          usePortal
        >
          {saveEpisodeError ? (
            <p className={css.error}>
              <FormattedMessage
                id={
                  saveEpisodeError?.error === 'free_plan_attendee_hours'
                    ? 'EditLiveRoomSchedulesPanel.usageLimitError'
                    : saveEpisodeError?.error === 'free_plan_episode_length'
                    ? freeEpisodeLengthMessageId()
                    : 'EditLiveRoomSchedulesPanel.saveEpisodeFailed'
                }
                values={
                  usage
                    ? { used: usageHours(usage.usedMinutes), limit: usageHours(usage.limitMinutes) }
                    : { used: 10, limit: 10 }
                }
              />
            </p>
          ) : null}
          <EditLiveRoomSchedulesForm
            formId="EditLiveRoomSchedulesForm"
            onSubmit={saveException}
            initialValues={episodeFormInitialValues}
            repeatOptions={repeatOptions}
            accessOptions={accessOptions}
            membershipTiers={publicData?.memberships || []}
            timeZone={defaultTimeZone()}
            availabilityExceptions={sortedAvailabilityExceptions}
            updateInProgress={updateInProgress}
            addExceptionInProgress={addExceptionInProgress || addRepeatingExceptionsInProgress}
            fetchErrors={errors}
            durationConfigOptions={durationConfigOptions}
          />
        </Modal>
      ) : null}
      {onManageDisableScrolling && rescheduleTarget ? (
        <Modal
          id="RescheduleEpisodeModal"
          isOpen={!!rescheduleTarget}
          onClose={closeReschedule}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          usePortal
        >
          <h2 className={css.rescheduleTitle}>
            <FormattedMessage
              id="EditLiveRoomSchedulesPanel.rescheduleTitle"
              values={{ title: rescheduleTarget.metadata?.attributes?.title || title }}
            />
          </h2>
          <RescheduleEpisodeForm
            initialValues={{
              start: toLocalInputValue(rescheduleTarget.exception.attributes.start),
            }}
            onSubmit={submitReschedule}
            onCancel={closeReschedule}
            inProgress={rescheduleInProgress}
            submitError={rescheduleError}
            reservedCount={
              publicData?.episodeRsvpCounts?.[
                rescheduleTarget.metadata?.attributes?.episodeId ||
                  rescheduleTarget.exception.id.uuid
              ] || 0
            }
          />
        </Modal>
      ) : null}
    </main>
  );
};

EditLiveRoomSchedulesPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
  currentUser: null,
};

EditLiveRoomSchedulesPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onNextTab: func.isRequired,
  onUpdateListing: func.isRequired,
  onCreateMeeting: func.isRequired,
  submitButtonText: string.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
  currentUser: object,

  // from intl
  intl: intlShape.isRequired,
};

export default injectIntl(EditLiveRoomSchedulesPanel);
